import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A `?next=` value, kept only when it is a path on this origin.
 *
 * **This was a blocklist until 2026-08-25 and it leaked** (DevGuru, review of
 * `#15`). The comment claimed the accepting side was written; the code kept
 * anything starting with `/` whose second character was not `/` or `\`, which
 * is the rejecting side wearing the other one's words. Measured through the
 * WHATWG URL parser — the one a browser uses for `location.assign`, `href` and
 * `pushState`:
 *
 * | `?next=`          | old predicate | browser reads it as   |
 * | ----------------- | ------------- | --------------------- |
 * | `//evil.com`      | dropped       | —                     |
 * | `/%09/evil.com`   | **kept**      | `https://evil.com/`   |
 * | `/%0A/evil.com`   | **kept**      | `https://evil.com/`   |
 * | `/%0D/evil.com`   | **kept**      | `https://evil.com/`   |
 *
 * Tab, LF and CR are *removed* while a URL is parsed, so `/<TAB>/evil.com` is
 * `//evil.com` by the time it is used — and `URLSearchParams` hands us the
 * decoded character, so it arrives here as a real tab. A signed-in person
 * opening `https://<console>/login?next=/%09/evil.com` was carried to another
 * origin from our address.
 *
 * So it is now an accepting predicate over a closed set, the same shape and the
 * same character sets as `safeRedirectPath` in the API (`redirect.ts`) — the
 * two are the same decision on two sides of one redirect, and the API's is the
 * one that has to agree with it, because a value refused there falls back to
 * `/` and the authorization is lost.
 *
 * The query half is admitted deliberately: the consent screen cannot be
 * addressed without it (`/consent?client_id=…`). It is checked as it arrived
 * rather than decoded, because a query's percent-encoding is its value.
 */
const RELATIVE_PATH = /^\/[A-Za-z0-9._~/-]*$/;
const QUERY_STRING = /^[A-Za-z0-9._~/?:@!$'()*+,;=&%-]*$/;

export function sameOriginPath(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;

  const cut = value.indexOf("?");
  const rawPath = cut === -1 ? value : value.slice(0, cut);
  const rawQuery = cut === -1 ? "" : value.slice(cut + 1);

  let path: string;
  try {
    path = decodeURIComponent(rawPath);
  } catch {
    return undefined;
  }

  // Encoded twice means the value keeps unwrapping; what we hand to the router
  // has to be final.
  try {
    if (decodeURIComponent(path) !== path) return undefined;
  } catch {
    return undefined;
  }

  if (!RELATIVE_PATH.test(path)) return undefined;
  if (!QUERY_STRING.test(rawQuery)) return undefined;

  try {
    decodeURIComponent(rawQuery);
  } catch {
    return undefined;
  }

  // Protocol-relative on the path is another origin. Inside a query it is two
  // ordinary characters.
  if (path.startsWith("//")) return undefined;

  return cut === -1 ? path : `${path}?${rawQuery}`;
}

/** Where Better Auth's authorization endpoint lives, through the console's proxy. */
const AUTHORIZE_PATH = "/v1/auth/oauth2/authorize";

/** The console page that unpacks an interrupted authorization and resumes it. */
const RESUME_PATH = "/continue";

/**
 * The authorization query, packed so it survives being a `callbackURL`.
 *
 * **Measured on staging by bisection**, sending magic-link verifications with
 * differently shaped return addresses and reading which came back refused:
 *
 *     /                                            accepted
 *     /v1/auth/oauth2/authorize?a=b                accepted
 *     /v1/auth/oauth2/authorize?scope=a+b          accepted
 *     /v1/auth/oauth2/authorize?scope=sites%3Aread REFUSED
 *
 * The difference is a percent-encoded **colon**. Better Auth matches a relative
 * `callbackURL` against a character class that has no `:` in it, and by the time
 * it is matched the value has been decoded once more than that class expects —
 * so `%3A` is a colon and the whole address is refused. Our authorization query
 * is full of them: `scope=sites:read`, `redirect_uri=https://…`,
 * `client_id=https://…`.
 *
 * Passing the absolute console URL instead does not help, and the reason is our
 * own guard rather than the library's: the API's gate keeps only relative paths
 * (`safeRedirectPath`). Measured in the same series — an absolute address is
 * replaced by the default landing, *including* one pointing at another origin,
 * which is the property that guard exists for and it holds.
 *
 * What is left is a query with no colons in it, so the real one is base64url —
 * an alphabet of `A-Za-z0-9-_`, every character of which the library accepts.
 * It is unpacked by {@link resumedAuthorization} on a page of ours.
 *
 * Not encryption and not a signature: the query already carries `sig` from the
 * authorization server, which is what makes it tamper-evident. This is only a
 * spelling that survives the journey.
 */
export function authorizationReturn(search: string): string | undefined {
  const query = search.startsWith("?") ? search.slice(1) : search;
  if (!query) return undefined;

  const params = new URLSearchParams(query);
  if (!params.get("client_id") || !params.get("response_type")) return undefined;

  return `${RESUME_PATH}?q=${base64Url(query)}`;
}

/**
 * The authorization to resume, unpacked from what {@link authorizationReturn}
 * packed.
 *
 * The **path is ours and the blob only supplies the query** — that ordering is
 * the whole of this function's safety. A packed value that decoded to a path
 * would be an open redirect wearing an encoding; here the worst a bad blob can
 * do is produce a query the authorization server refuses, which it will, since
 * `sig` covers the parameters.
 */
export function resumedAuthorization(packed: unknown): string | undefined {
  if (typeof packed !== "string" || packed === "") return undefined;

  let query: string;
  try {
    query = fromBase64Url(packed);
  } catch {
    return undefined;
  }

  const params = new URLSearchParams(query);
  if (!params.get("client_id") || !params.get("response_type")) return undefined;

  return `${AUTHORIZE_PATH}?${query}`;
}

/**
 * Where a Google round that did not finish comes back to.
 *
 * The provider needs an address for the failed case as much as for the happy
 * one, and left unset the library uses its own `/v1/auth/error`, which is the
 * API's page: somebody who cancelled at Google would land on a document this
 * console does not own, with no way back into what they were doing.
 *
 * It carries `landing` along as `?next=` for the one case where losing it
 * matters — a sign-in that interrupted an authorization. Coming back to a bare
 * `/login` there would make the retry finish on the home page and leave the
 * client waiting, which is the same fault `authorizationReturn` above exists
 * for. `/` is the ordinary case and is the login screen's own default, so it is
 * left off rather than spelled out.
 *
 * Encoded, because `landing` may itself be a path *with* a query (the
 * authorization return is), and an unencoded `&` would end this parameter and
 * start another one.
 */
export function signInRetryPath(landing: string): string {
  return landing === "/" ? "/login" : `/login?next=${encodeURIComponent(landing)}`;
}

/** base64url, which is `btoa` with the two characters a URL argues about swapped out. */
function base64Url(value: string): string {
  return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): string {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  return atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
}
